/**
 * Settings > Scope structure: default structure and the heading list on the left, the
 * account heading text style (with its preview) on the right. Presentational only: the
 * preferences, save and flash state stay in V2SettingsPage so an unsaved edit survives a
 * tab switch.
 */
import type { ScopeConfig, ScopeHeading } from '../../../types';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import HeadingStyleControls from '../HeadingStyleControls'; // PRP-MCP-15 section-heading style
import { IconCheck, IconChevd, IconWarn } from '../icons';
import { FieldLabel, Section, type SaveFlash } from './SettingsFrame';

interface Props {
  config: ScopeConfig;
  loading: boolean;
  saving: boolean;
  flash: SaveFlash;
  onSave: () => void;
  updateScope: (_patch: Partial<ScopeConfig>) => void;
  setHeadings: (_headings: ScopeHeading[]) => void;
  moveHeading: (_index: number, _dir: -1 | 1) => void;
}

export default function ScopeStructurePanel({
  config,
  loading,
  saving,
  flash,
  onSave,
  updateScope,
  setHeadings,
  moveHeading,
}: Props) {
  return (
    <Section
      title="Scope structure"
      sub="How generated scopes are organized. Can be overridden per project or carrier."
      footer={
        <Button onClick={onSave} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save structure'}
        </Button>
      }
    >
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-x-10 gap-y-5 lg:grid-cols-2">
            <div className="space-y-5 min-w-0">
              <div className="max-w-[360px]">
                <FieldLabel htmlFor="structure-type">Default structure</FieldLabel>
                <Select
                  id="structure-type"
                  value={config.structure_type}
                  onChange={(e) =>
                    updateScope({
                      structure_type: e.target.value as ScopeConfig['structure_type'],
                    })
                  }
                >
                  <option value="top_down">Top-Down (Standard)</option>
                  <option value="bottom_up">Bottom-Up (for Flood Claims)</option>
                </Select>
                <p className="mt-[6px] mb-0 text-[11.5px] text-[var(--tx-faint)]">
                  Choose &ldquo;Bottom-Up&rdquo; for flood claims to start from the floor.
                </p>
              </div>

              <div>
                <FieldLabel>Headings</FieldLabel>
                <p className="mt-0 mb-[8px] text-[11.5px] text-[var(--tx-faint)]">
                  Reorder, rename, or disable the headings used to group line items.
                </p>
                <div className="rounded-[var(--r-md)] border border-[var(--bd-subtle)] divide-y divide-[var(--bd-subtle)]">
                  {config.headings.length === 0 && (
                    <p className="m-0 px-3 py-4 text-[12.5px] text-[var(--tx-faint)]">
                      No headings configured.
                    </p>
                  )}
                  {config.headings.map((h, i) => (
                    <div key={h.id} className="flex items-center gap-2 px-3 py-2">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => moveHeading(i, -1)}
                          disabled={i === 0}
                          className="text-[var(--tx-faint)] hover:text-[var(--tx-hi)] disabled:opacity-30 [&_svg]:w-4 [&_svg]:h-4"
                          aria-label={`Move ${h.name} up`}
                        >
                          <IconChevd className="rotate-180" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveHeading(i, 1)}
                          disabled={i === config.headings.length - 1}
                          className="text-[var(--tx-faint)] hover:text-[var(--tx-hi)] disabled:opacity-30 [&_svg]:w-4 [&_svg]:h-4"
                          aria-label={`Move ${h.name} down`}
                        >
                          <IconChevd />
                        </button>
                      </div>
                      <Input
                        value={h.name}
                        onChange={(e) => {
                          const list = [...config.headings];
                          list[i] = { ...h, name: e.target.value };
                          setHeadings(list);
                        }}
                        className="flex-1"
                      />
                      <label className="flex items-center gap-[6px] text-[12px] text-[var(--tx-lo)] whitespace-nowrap">
                        <Switch
                          checked={h.enabled}
                          onCheckedChange={(checked) => {
                            const list = [...config.headings];
                            list[i] = { ...h, enabled: checked };
                            setHeadings(list);
                          }}
                        />
                        Enabled
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="min-w-0">
              <FieldLabel>Heading text style in exported ESX</FieldLabel>
              <HeadingStyleControls
                value={config.heading_style}
                onChange={(heading_style) => updateScope({ heading_style })}
              />
            </div>
          </div>

          {flash && (
            <Banner
              tone={flash.kind === 'ok' ? 'ok' : 'warn'}
              icon={flash.kind === 'ok' ? <IconCheck /> : <IconWarn />}
            >
              {flash.msg}
            </Banner>
          )}
        </div>
      )}
    </Section>
  );
}
